import type React from "react";
import { createContext, useContext } from "react";
import { Pressable, Text, View } from "react-native";
import Modal from "react-native-modal";
import CloseIcon from "@/assets/svgs/close-icon";
import ResponsiveContent from "../shared/responsive-content";

type AlertDialogContextValue = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const AlertDialogContext = createContext<AlertDialogContextValue | null>(null);

function useAlertDialog() {
  const context = useContext(AlertDialogContext);
  if (!context) {
    throw new Error("AlertDialog components must be used within AlertDialog");
  }
  return context;
}

type AlertDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
};

function AlertDialog({ open, onOpenChange, children }: AlertDialogProps) {
  return (
    <AlertDialogContext.Provider value={{ open, onOpenChange }}>
      {children}
    </AlertDialogContext.Provider>
  );
}

type AlertDialogContentProps = {
  children: React.ReactNode;
  showCloseButton?: boolean;
  className?: string;
};

function AlertDialogContent({
  children,
  showCloseButton = false,
  className,
}: AlertDialogContentProps) {
  const { open, onOpenChange } = useAlertDialog();

  return (
    <Modal
      animationIn="fadeIn"
      animationOut="fadeOut"
      animationInTiming={150}
      animationOutTiming={150}
      isVisible={open}
      onBackdropPress={() => onOpenChange(false)}
      onDismiss={() => onOpenChange(false)}
      backdropOpacity={0.8}
      useNativeDriver
      useNativeDriverForBackdrop
      hideModalContentWhileAnimating
    >
      <ResponsiveContent>
        <View
          className={`mx-4 bg-white rounded-2xl p-6 shadow-xl max-w-lg w-full self-center ${className ?? ""}`}
        >
          {showCloseButton && (
            <View className="absolute right-4 top-4 z-10">
              <Pressable
                onPress={() => onOpenChange(false)}
                className="p-1 rounded-sm opacity-70 hover:opacity-100 active:opacity-50"
              >
                <CloseIcon pathClassName="stroke-gray-500" className="size-4" />
              </Pressable>
            </View>
          )}
          {children}
        </View>
      </ResponsiveContent>
    </Modal>
  );
}

type AlertDialogHeaderProps = {
  children: React.ReactNode;
  className?: string;
};

function AlertDialogHeader({ children, className }: AlertDialogHeaderProps) {
  return (
    <View className={`flex flex-col gap-2 ${className ?? ""}`}>{children}</View>
  );
}

type AlertDialogFooterProps = {
  children: React.ReactNode;
  className?: string;
};

function AlertDialogFooter({ children, className }: AlertDialogFooterProps) {
  return (
    <View
      className={`flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-6 ${className ?? ""}`}
    >
      {children}
    </View>
  );
}

type AlertDialogTitleProps = {
  children: React.ReactNode;
  className?: string;
};

function AlertDialogTitle({ children, className }: AlertDialogTitleProps) {
  return (
    <Text
      className={`text-lg font-semibold font-geologica-semibold text-gray-900 ${className ?? ""}`}
    >
      {children}
    </Text>
  );
}

type AlertDialogDescriptionProps = {
  children: React.ReactNode;
  className?: string;
};

function AlertDialogDescription({
  children,
  className,
}: AlertDialogDescriptionProps) {
  return (
    <Text
      className={`text-sm font-geologica-regular text-gray-500 ${className ?? ""}`}
    >
      {children}
    </Text>
  );
}

type AlertDialogActionProps = {
  children: React.ReactNode;
  onPress?: () => void;
  className?: string;
  variant?: "default" | "destructive";
  disabled?: boolean;
};

function AlertDialogAction({
  children,
  onPress,
  className,
  variant = "default",
  disabled = false,
}: AlertDialogActionProps) {
  const { onOpenChange } = useAlertDialog();

  const handlePress = () => {
    onPress?.();
    onOpenChange(false);
  };

  const baseStyles =
    "px-4 py-2.5 rounded-lg items-center justify-center active:opacity-80";
  const variantStyles =
    variant === "destructive"
      ? "bg-red-500 hover:bg-red-600"
      : "bg-teachLoop-accentPrimary hover:bg-teal-700";
  const disabledStyles = disabled ? "opacity-50" : "";

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      className={`${baseStyles} ${variantStyles} ${disabledStyles} ${className ?? ""}`}
    >
      <Text className="text-sm font-medium font-geologica-medium text-white">
        {children}
      </Text>
    </Pressable>
  );
}

type AlertDialogCancelProps = {
  children: React.ReactNode;
  onPress?: () => void;
  className?: string;
  disabled?: boolean;
};

function AlertDialogCancel({
  children,
  onPress,
  className,
  disabled = false,
}: AlertDialogCancelProps) {
  const { onOpenChange } = useAlertDialog();

  const handlePress = () => {
    onPress?.();
    onOpenChange(false);
  };

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      className={`px-4 py-2.5 rounded-lg items-center justify-center border border-gray-200 bg-white hover:bg-gray-50 active:bg-gray-100 ${disabled ? "opacity-50" : ""} ${className ?? ""}`}
    >
      <Text className="text-sm font-medium font-geologica-medium text-gray-700">
        {children}
      </Text>
    </Pressable>
  );
}

export {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
